/* ==========================================================================
   ZAMU AFRICA — Collection filtering & sorting
   Uses Shopify's native storefront filters (Search & Discovery) plus the
   Section Rendering API, so filter logic, counts and availability all come
   from Shopify rather than being recomputed client-side.

   Without JavaScript the same form submits normally and Shopify renders the
   filtered page — progressive enhancement, not a hard dependency.
   ========================================================================== */

(function () {
  'use strict';

  var ZAMU = (window.ZAMU = window.ZAMU || {});
  var parser = new DOMParser();

  function CollectionFilters(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.form = root.querySelector('[data-facet-form]');
    this.grid = root.querySelector('[data-product-grid-wrap]');
    this.pending = null;

    if (!this.form || !this.grid || !this.sectionId) return;

    this.onInput = ZAMU.debounce(this.apply.bind(this), 320);
    this.bind();
    this.initMobileToggle();

    var self = this;
    window.addEventListener('popstate', function () {
      self.render(window.location.search, false);
    });
  }

  CollectionFilters.prototype.bind = function () {
    var self = this;

    this.form.addEventListener('change', function (event) {
      var target = event.target;
      // Price range inputs are debounced; everything else applies immediately.
      if (target.matches('input[type="number"]')) {
        self.onInput();
      } else {
        self.apply();
      }
    });

    this.form.addEventListener('input', function (event) {
      if (event.target.matches('input[type="number"]')) self.onInput();
    });

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.apply();
    });

    // Delegated so it survives grid re-renders.
    this.root.addEventListener('click', function (event) {
      var link = event.target.closest('[data-facet-link]');
      if (!link) return;
      event.preventDefault();

      // The href is generated by Liquid from Shopify's own filter objects.
      var url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;

      self.render(url.search, true);
    });
  };

  CollectionFilters.prototype.initMobileToggle = function () {
    var toggle = this.root.querySelector('[data-facet-toggle]');
    var panel = this.root.querySelector('[data-facets]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', function () {
      var open = panel.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  };

  CollectionFilters.prototype.apply = function () {
    var params = new URLSearchParams(new FormData(this.form));

    // Drop empties so the URL stays readable and cacheable.
    Array.prototype.slice.call(params.keys()).forEach(function (key) {
      var values = params.getAll(key).filter(function (value) {
        return value !== '';
      });
      params.delete(key);
      values.forEach(function (value) {
        params.append(key, value);
      });
    });

    this.render('?' + params.toString(), true);
  };

  CollectionFilters.prototype.render = function (search, pushState) {
    var self = this;
    var separator = search.indexOf('?') === 0 ? '&' : '?';
    var url = window.location.pathname + search + separator + 'section_id=' + encodeURIComponent(this.sectionId);

    this.grid.setAttribute('aria-busy', 'true');

    if (this.pending) this.pending.abort();
    var controller = new AbortController();
    this.pending = controller;

    fetch(url, { signal: controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Filter request failed');
        return response.text();
      })
      .then(function (markup) {
        var incoming = parser.parseFromString(markup, 'text/html');

        // Swap the grid and the facet panel; nodes are moved, not stringified.
        ['[data-product-grid-wrap]', '[data-facets-inner]', '[data-active-facets]', '[data-collection-count]'].forEach(
          function (selector) {
            var source = incoming.querySelector(selector);
            var target = self.root.querySelector(selector);
            if (!source || !target) return;
            target.replaceChildren.apply(target, Array.prototype.slice.call(source.childNodes));
          }
        );

        if (pushState) {
          // Same-origin path built from location.pathname — no open redirect.
          window.history.pushState({ search: search }, '', window.location.pathname + search);
        }

        ZAMU.initReveal(self.root);
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        // Fall back to a full page load so the shopper still gets results.
        window.location.assign(window.location.pathname + search);
      })
      .finally(function () {
        self.grid.removeAttribute('aria-busy');
        self.pending = null;
      });
  };

  /* ------------------------------------------------------------------------
     Sort dropdown — plain form control, applied through the same pipeline
     ------------------------------------------------------------------------ */

  function initSort(root) {
    var select = root.querySelector('[data-sort-select]');
    if (!select) return;

    select.addEventListener('change', function () {
      var form = select.closest('form');
      if (form) form.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function init(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-collection-section]'),
      function (root) {
        new CollectionFilters(root);
        initSort(root);
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
